import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiBaseUrl } from "@/lib/api";

const WSCtx = createContext(null);

export function WSProvider({ children }) {
    const { user } = useAuth();
    const wsRef = useRef(null);
    const listenersRef = useRef(new Set());
    const [connected, setConnected] = useState(false);

    const connect = useCallback(() => {
        if (!user) return;
        const backend = apiBaseUrl();
        const wsUrl = backend.replace(/^http/, "ws") + "/api/ws";
        try {
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;
            ws.onopen = () => setConnected(true);
            ws.onclose = () => {
                setConnected(false);
                wsRef.current = null;
                // Reconnect after 2s if user still logged in
                setTimeout(() => {
                    if (user) connect();
                }, 2000);
            };
            ws.onerror = () => {
                // let onclose handle reconnect
            };
            ws.onmessage = (ev) => {
                try {
                    const msg = JSON.parse(ev.data);
                    listenersRef.current.forEach((cb) => {
                        try {
                            cb(msg);
                        } catch {
                            /* ignore */
                        }
                    });
                } catch {
                    /* ignore */
                }
            };
        } catch {
            /* ignore */
        }
    }, [user]);

    useEffect(() => {
        if (user) connect();
        return () => {
            if (wsRef.current) {
                try {
                    wsRef.current.close();
                } catch {
                    /* ignore */
                }
                wsRef.current = null;
            }
            setConnected(false);
        };
    }, [user, connect]);

    const subscribe = useCallback((fn) => {
        listenersRef.current.add(fn);
        return () => listenersRef.current.delete(fn);
    }, []);

    return <WSCtx.Provider value={{ connected, subscribe }}>{children}</WSCtx.Provider>;
}

export function useWS() {
    const ctx = useContext(WSCtx);
    if (!ctx) throw new Error("useWS must be used inside WSProvider");
    return ctx;
}
